import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { OpenCaseBarItem, OpenCaseBarService } from '#services';

@Component({
  selector: 'app-open-case-bar',
  imports: [TranslatePipe],
  templateUrl: './open-case-bar.html',
  styleUrl: './open-case-bar.scss',
})
export class OpenCaseBar implements AfterViewInit {
  private _router = inject(Router);
  private _destroyRef = inject(DestroyRef);
  private _openCaseBarService = inject(OpenCaseBarService);
  private _track = viewChild<ElementRef<HTMLElement>>('track');
  private _expanded = signal(false);
  private _canToggleExpansion = signal(false);

  cases = this._openCaseBarService.cases;
  activeCaseId = this._openCaseBarService.activeCaseId;
  hasCases = this._openCaseBarService.hasCases;
  expanded = this._expanded.asReadonly();
  canToggleExpansion = this._canToggleExpansion.asReadonly();

  constructor() {
    effect(() => {
      if (!this.hasCases()) {
        this._expanded.set(false);
        this._canToggleExpansion.set(false);
        return;
      }

      this.cases();
      this._scheduleOverflowCheck();
    });
  }

  ngAfterViewInit(): void {
    const trackElement = this._track()?.nativeElement;

    if (trackElement && typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() =>
        this._updateOverflowState(),
      );

      resizeObserver.observe(trackElement);
      this._destroyRef.onDestroy(() => resizeObserver.disconnect());
    }

    this._scheduleOverflowCheck();
  }

  getTooltip(caseItem: OpenCaseBarItem): string {
    return `${caseItem.label} (${caseItem.caseId})`;
  }

  toggleExpanded(): void {
    this._expanded.update((expanded) => !expanded);
  }

  onSelectCase(caseItem: OpenCaseBarItem): void {
    this._openCaseBarService.activateCase(caseItem.id);

    if (!this._router.url.startsWith(caseItem.route)) {
      void this._router.navigate([caseItem.route], {
        queryParams: caseItem.queryParams ?? {},
        state: {
          activeNavPath: caseItem.activeNavPath,
        },
      });
    }
  }

  onCloseCase(event: MouseEvent, caseId: string): void {
    event.stopPropagation();
    this._openCaseBarService.closeCase(caseId);
  }

  onClearAll(): void {
    this._openCaseBarService.clearCases();
  }

  private _scheduleOverflowCheck(): void {
    queueMicrotask(() => this._updateOverflowState());
  }

  private _updateOverflowState(): void {
    const trackElement = this._track()?.nativeElement;

    if (!trackElement) {
      this._canToggleExpansion.set(false);
      this._expanded.set(false);
      return;
    }

    const pills = trackElement.querySelectorAll<HTMLElement>('.open-case-pill');

    if (pills.length === 0) {
      this._canToggleExpansion.set(false);
      this._expanded.set(false);
      trackElement.style.removeProperty('--open-case-track-collapsed-height');
      return;
    }

    const firstRowHeight = pills[0].offsetHeight;
    const multiRowContent = trackElement.scrollHeight > firstRowHeight + 4;

    trackElement.style.setProperty(
      '--open-case-track-collapsed-height',
      `${firstRowHeight}px`,
    );

    this._canToggleExpansion.set(multiRowContent);

    if (!multiRowContent) {
      this._expanded.set(false);
    }
  }
}
